import React, { useState, useEffect } from 'react';
import '../styles/manual.css';
import { useTranslation } from '../hooks/useTranslation';
import { MANUAL } from '../content/manualData';
import SEO from '../components/SEO';

const Manual = () => {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const [currentInstruction, setCurrentInstruction] = useState(null);
  const [expandedDevices, setExpandedDevices] = useState({});
  const [selectedDevice, setSelectedDevice] = useState('android');
  const [selectedApp, setSelectedApp] = useState(null);

  // Generate deviceApps from MANUAL data
  const deviceApps = MANUAL.devices.reduce((acc, device) => {
    acc[device.id] = device.apps.map(app => ({
      value: app.id,
      text: app.name[language] || app.name.en
    }));
    return acc;
  }, {});

  useEffect(() => {
    // Initialize with first app for default device
    const firstApp = deviceApps['android']?.[0]?.value || null;
    setSelectedApp(firstApp);
    if (firstApp) setCurrentInstruction(firstApp);
  }, []);

  const toggleDevice = (deviceId) => {
    setExpandedDevices(prev => ({
      ...prev,
      [deviceId]: !prev[deviceId]
    }));
  };

  const showInstruction = (instructionId) => {
    setCurrentInstruction(instructionId);
  };

  const handleDeviceChange = (e) => {
    const dev = e.target.value;
    setSelectedDevice(dev);
    const first = deviceApps[dev]?.[0]?.value || null;
    setSelectedApp(first);
    if (first) setCurrentInstruction(first);
  };

  const handleAppChange = (e) => {
    const app = e.target.value;
    setSelectedApp(app);
    if (app) setCurrentInstruction(app);
  };

  // Helper to get localized text
  const getLocalizedText = (textObj) => {
    return textObj?.[language] || textObj?.en || '';
  };

  // Find current app data
  const getCurrentAppData = () => {
    if (!currentInstruction) return null;
    
    for (const device of MANUAL.devices) {
      for (const app of device.apps) {
        if (app.id === currentInstruction) {
          return {
            device: device.name,
            app: app.name,
            steps: app.steps
          };
        }
      }
    }
    return null;
  };

  const InstructionStep = ({ step, number }) => (
    <div className="step">
      <div className="step-title">
        <span className="step-number">{number}</span>
        {getLocalizedText(step.title)}
      </div>
      <div className="step-content">
        {step.content && <p>{getLocalizedText(step.content)}</p>}
        
        {step.buttons && (
          <div className="download-buttons">
            {step.buttons.map((button, index) => (
              <a
                key={index}
                href={button.url}
                target="_blank"
                rel="noopener noreferrer"
                className={button.className || 'download-btn'}
              >
                {getLocalizedText(button.text)}
              </a>
            ))}
          </div>
        )}

        {step.note && (
          <div className="note">
            <div className="note-title">{getLocalizedText(step.note.title)}</div>
            <p>{getLocalizedText(step.note.content)}</p>
          </div>
        )}

        {step.warning && (
          <div className="warning">
            <div className="warning-title">{getLocalizedText(step.warning.title)}</div>
            <p>{getLocalizedText(step.warning.content)}</p>
          </div>
        )}

        {step.images && step.images.map((image, index) => (
          <div key={index} style={{ textAlign: 'center', margin: '20px 0' }}>
            <img
              src={image.src}
              alt={getLocalizedText(image.alt)}
              className={image.className || 'step-image'}
            />
          </div>
        ))}
      </div>
    </div>
  );

  const currentAppData = getCurrentAppData();

  return (
    <div className="manual-page">
      <SEO
        title="Инструкции по настройке VPN — Diamond VPN"
        description="Пошаговые инструкции по настройке Diamond VPN для Android, iOS, Windows и других устройств. Простая установка и быстрый старт за несколько минут."
        ogImage="https://diamondvpn.online/preview.jpg"
      />
      {/* Animated diamonds */}
      <div className="diamond" style={{ top: '20%', left: '10%' }}></div>
      <div className="diamond" style={{ top: '60%', left: '85%', animationDelay: '-2s' }}></div>
      <div className="diamond" style={{ top: '30%', left: '70%', animationDelay: '-4s' }}></div>
      <div className="diamond" style={{ top: '80%', left: '20%', animationDelay: '-1s' }}></div>
      <div className="diamond" style={{ top: '10%', left: '50%', animationDelay: '-3s' }}></div>

      {/* Mobile device/app selector */}
      <div className="container">

        {/* Sidebar navigation */}
        <nav className="sidebar">
          <div className="sidebar-header">
            <div className="sidebar-title">{t('manual.sidebar.title')}</div>
            <div className="sidebar-subtitle">{t('manual.sidebar.subtitle')}</div>
          </div>

          {MANUAL.devices.map((device) => (
            <div key={device.id} className="nav-section">
              <div 
                className={`nav-device ${expandedDevices[device.id] ? 'active' : ''}`} 
                onClick={() => toggleDevice(device.id)}
              >
                <div className="device-info">
                  <i className={`${device.icon} device-icon`}></i>
                  <span className="device-name">{getLocalizedText(device.name)}</span>
                </div>
                <i className={`fas fa-chevron-right expand-icon ${expandedDevices[device.id] ? 'expanded' : ''}`}></i>
              </div>
              <div className={`nav-apps ${expandedDevices[device.id] ? 'expanded' : ''}`}>
                {device.apps.map((app) => (
                  <div 
                    key={app.id}
                    className={`nav-app ${currentInstruction === app.id ? 'active' : ''}`}
                    onClick={() => showInstruction(app.id)}
                  >
                    <div className="app-icon">{app.icon}</div>
                    {getLocalizedText(app.name)}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Main content */}
        <main className="main-content-manual">
          <div className="content-header">
            <h1 className="content-title">
              {currentAppData
                ? t('manual.contentTitle', {
                    app: getLocalizedText(currentAppData.app),
                    device: getLocalizedText(currentAppData.device)
                  })
                : t('manual.selectDevice')}
            </h1>
            <p className="content-subtitle">
              {currentInstruction 
                ? t('manual.stepByStep')
                : t('manual.selectPrompt')}
            </p>
          </div>
          <div className="mobile-device-selector">
            <select
              className="device-dropdown"
              value={selectedDevice}
              onChange={handleDeviceChange}
            >
              {MANUAL.devices.map((device) => (
                <option key={device.id} value={device.id}>
                  {getLocalizedText(device.name)}
                </option>
              ))}
            </select>
            <select
              className="app-dropdown"
              value={selectedApp || ''}
              onChange={handleAppChange}
            >
              {(deviceApps[selectedDevice] || []).map((app) => (
                <option key={app.value} value={app.value}>
                  {app.text}
                </option>
              ))}
            </select>
          </div>
          {/* Instructions */}
          {currentAppData && (
            <div className="instruction-section active">
              {currentAppData.steps.map((step, index) => (
                <InstructionStep 
                  key={index} 
                  step={step} 
                  number={step.number || index + 1} 
                />
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Manual;
